import React, { useState, useEffect } from 'react';
import { makeStyles } from '@mui/styles';
import { Box, Typography } from '@mui/material';
import Chip from 'components/Chip';

const useStyles = makeStyles((theme) => ({
  solutionsContainer: {
    width: '100%',
    background: 'linear-gradient(180deg, #0A3F63 0%, #122A3A 100%)',
    padding: theme.spacing(20, 37.5),
    position: 'relative',
    overflow: 'hidden',
    [theme.breakpoints.down('xl')]: {
      padding: theme.spacing(20, 15),
    },
    [theme.breakpoints.down('lg')]: {
      padding: theme.spacing(15, 15),
    },
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(12, 10),
    },
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(10, 5),
    },
  },
  contentWrapper: {
    maxWidth: '1200px',
    margin: '0 auto',
    position: 'relative',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(10),
    width: '100%',
    boxSizing: 'border-box',
    [theme.breakpoints.down('lg')]: {
      gap: theme.spacing(6),
    },
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
      maxWidth: '100%',
    },
  },
  leftContentWrapper: {
    flex: 1,
    maxWidth: '50%',
    boxSizing: 'border-box',
    [theme.breakpoints.down('md')]: {
      maxWidth: '100%',
      width: '100%',
    },
  },
  solutionsBadge: {
    marginBottom: theme.spacing(6),
    [theme.breakpoints.down('md')]: {
      marginBottom: theme.spacing(4),
    },
  },
  heading: {
    ...theme.typography['h2-bold'],
    color: theme.palette.shades['clr-white-900'],
    marginBottom: theme.spacing(10),
    width: '100%',
    wordWrap: 'break-word',
    [theme.breakpoints.down('lg')]: {
      ...theme.typography['h3-bold'],
      marginBottom: theme.spacing(8),
    },
    [theme.breakpoints.down('md')]: {
      ...theme.typography['h4-bold'],
      marginBottom: theme.spacing(6),
    },
  },
  featuresContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(8),
    width: '100%',
    [theme.breakpoints.down('lg')]: {
      gap: theme.spacing(6),
    },
    [theme.breakpoints.down('md')]: {
      gap: theme.spacing(5),
    },
  },
  featureItem: {
    display: 'flex',
    gap: theme.spacing(4),
    alignItems: 'flex-start',
    position: 'relative',
    width: '100%',
    boxSizing: 'border-box',
    [theme.breakpoints.down('md')]: {
      gap: theme.spacing(3),
    },
    [theme.breakpoints.down('sm')]: {
      gap: theme.spacing(2),
    },
  },
  iconColumn: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    position: 'relative',
  },
  iconWrapper: {
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    zIndex: 2,
    transition: 'border 0.5s ease-in-out',
    [theme.breakpoints.down('md')]: {
      width: '20px',
      height: '20px',
    },
    [theme.breakpoints.down('sm')]: {
      width: '18px',
      height: '18px',
    },
  },
  connectingLine: {
    position: 'absolute',
    top: '24px',
    left: '50%',
    transform: 'translateX(-50%)',
    width: '0',
    height: '90px',
    borderLeft: '2px dashed rgba(255, 255, 255, 0.3)',
    zIndex: 0,
    [theme.breakpoints.down('lg')]: {
      height: '90px',
    },
    [theme.breakpoints.down('md')]: {
      top: '20px',
      height: '90px',
    },
    [theme.breakpoints.down('sm')]: {
      top: '18px',
      height: '90px',
    },
  },
  featureContent: {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
  },
  featureTitle: {
    ...theme.typography['h5-bold'],
    color: theme.palette.shades['clr-white-900'],
    marginBottom: theme.spacing(2),
    wordWrap: 'break-word',
    [theme.breakpoints.down('lg')]: {
      ...theme.typography['h6-bold'],
    },
    [theme.breakpoints.down('md')]: {
      ...theme.typography['body01-bold'],
      marginBottom: theme.spacing(1.5),
    },
  },
  featureDescription: {
    ...theme.typography['body01-regular'],
    color: 'rgba(255, 255, 255, 0.8)',
    lineHeight: 1.6,
    wordWrap: 'break-word',
    [theme.breakpoints.down('md')]: {
      ...theme.typography['body02-regular'],
    },
  },
  imageContainer: {
    flex: 1,
    maxWidth: '45%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    [theme.breakpoints.down('md')]: {
      maxWidth: '100%',
      width: '100%',
      marginTop: theme.spacing(6),
    },
  },
  animatedImage: {
    // The three screenshots have slightly different intrinsic ratios (0.63 to
    // 0.67), so the frame is fixed here and each card is fitted inside it whole
    // — the occupied box is identical on every slide and nothing shifts when
    // the active one changes. The frame matches the tallest card (case_card3),
    // so every card renders at full width and the two shorter ones simply get
    // a little empty space above and below.
    display: 'block',
    width: '100%',
    maxWidth: '400px',
    height: 'auto',
    aspectRatio: '0.627',
    objectFit: 'contain',
    objectPosition: 'center',
    transition: 'opacity 0.5s ease-in-out',
    borderRadius: '16px',
  },
}));

// Icon component - Blue circle with dotted white border (rendered via CSS in iconWrapper class)
const FeatureIcon = ({ isActive }) => (
  <Box
    sx={{
      width: '12px',
      height: '12px',
      borderRadius: '50%',
      backgroundColor: isActive ? '#3B82F6' : '#2563EB',
      opacity: isActive ? 1 : 0.5,
      transition: 'all 0.5s ease-in-out',
      boxShadow: isActive ? '0 0 12px rgba(59, 130, 246, 0.8)' : 'none',
    }}
  />
);

// Check icon for badges
const CheckIcon = ({ fillColor = '#047857' }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M11.1229 3.52405C11.2477 3.64891 11.3178 3.81824 11.3178 3.99479C11.3178 4.17135 11.2477 4.34067 11.1229 4.46553L5.79623 9.79218C5.67137 9.91701 5.50205 9.98713 5.32549 9.98713C5.14894 9.98713 4.97961 9.91701 4.85475 9.79218L2.19142 7.12886C2.07014 7.00328 2.00302 6.83509 2.00454 6.66051C2.00606 6.48593 2.07608 6.31893 2.19953 6.19548C2.32298 6.07203 2.48998 6.00201 2.66456 6.00049C2.83914 5.99898 3.00733 6.06609 3.13291 6.18737L5.32549 8.37996L10.1814 3.52405C10.3063 3.39922 10.4756 3.3291 10.6521 3.3291C10.8287 3.3291 10.998 3.39922 11.1229 3.52405Z"
      fill={fillColor}
    />
  </svg>
);

const CaseSolutions = () => {
  const classes = useStyles();
  const [activeIndex, setActiveIndex] = useState(0);

  const S3_BASE_URL = process.env.REACT_APP_ASSETS_URL;

  const features = [
    {
      title: 'Extensive Case Library',
      description: 'Choose from 150+ past interview cases across 14 case types, covering Product, Growth, EIR, Consulting, and diverse industries, and master case frameworks with Case Master AI.',
      image: `${S3_BASE_URL}/images/case-card4-final.png`,
    },
    {
      title: 'Deep AI-Powered Evaluation',
      description: 'Receive detailed feedback across 6+ performance parameters, including structure, logic, quantitative skills, creativity, communication, and overall case-solving approach.',
      image: `${S3_BASE_URL}/images/case_card2_hd.png`,
    },
    {
      title: 'Prepare Company specific Cases - from Logistics to Quick Commerce',
      description: 'Practice Company-Specific Cases Across Multiple Industries',
      image: `${S3_BASE_URL}/images/case_card3_hd.png`,
    },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveIndex((prevIndex) => (prevIndex + 1) % features.length);
    }, 3000); // Change every 3 seconds

    return () => clearInterval(interval);
  }, [features.length]);

  return (
    <Box className={classes.solutionsContainer}>
      <Box className={classes.contentWrapper}>
        {/* Left Content - 50% width */}
        <Box className={classes.leftContentWrapper}>
          {/* Solutions Badge */}
          <Box className={classes.solutionsBadge}>
            <Chip
              content="Solutions"
              startIcon={<CheckIcon fillColor="#FFFFFF" fontSize="20" />}
              color="#FFFFFF"
              bgColor="rgba(255, 255, 255, 0.15)"
              variant="body02-medium"
              sx={{
                borderRadius: '20px',
                padding: '8px 16px',
                backdropFilter: 'blur(10px)',
              }}
            />
          </Box>

          {/* Main Heading */}
          <Typography className={classes.heading}>
            How Case Master AI can be your interview buddy ?
          </Typography>

          {/* Features List */}
          <Box className={classes.featuresContainer}>
            {features.map((feature, index) => (
              <Box key={index} className={classes.featureItem}>
                {/* Icon Column with connecting line */}
                <Box className={classes.iconColumn}>
                  <Box
                    className={classes.iconWrapper}
                    sx={{
                      border: activeIndex === index
                        ? '2px dashed rgba(255, 255, 255, 0.9)'
                        : '2px dashed rgba(255, 255, 255, 0.5)'
                    }}
                  >
                    <FeatureIcon isActive={activeIndex === index} />
                  </Box>
                  {/* Show connecting line for all items except the last one */}
                  {index < features.length - 1 && (
                    <Box className={classes.connectingLine} />
                  )}
                </Box>

                {/* Feature Content */}
                <Box className={classes.featureContent}>
                  <Typography className={classes.featureTitle}>
                    {feature.title}
                  </Typography>
                  <Typography className={classes.featureDescription}>
                    {feature.description}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Animated Images - Right side */}
        <Box className={classes.imageContainer}>
          <img
            src={features[activeIndex].image}
            alt={`Case Master ${activeIndex + 1}`}
            className={classes.animatedImage}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default CaseSolutions;
